import React, { useState, useEffect, useCallback } from 'react';
import { studentsAPI } from '../services/api';

// Document submission tracker (Q2): the no-login link a student uses to see
// their own checklist + payments and upload against it.
const panelStyle = {
  padding: '14px 16px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  marginTop: 12,
};
const headingStyle = { color: '#9aa4c4', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 8 };

const ChecklistLinkPanel = ({ studentId }) => {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await studentsAPI.getChecklistLink(studentId);
      setLink(res.data);
    } catch (err) {
      setError('Could not load the student link.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate this link? The old link will stop working immediately.')) return;
    setBusy(true);
    setError('');
    try {
      const res = await studentsAPI.regenerateChecklistLink(studentId);
      setLink(res.data);
    } catch (err) {
      setError('Could not regenerate the link.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke this student's link? They will no longer be able to view or upload documents until you regenerate it.")) return;
    setBusy(true);
    setError('');
    try {
      const res = await studentsAPI.revokeChecklistLink(studentId);
      setLink(res.data);
    } catch (err) {
      setError('Could not revoke the link.');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = () => {
    if (!link?.link) return;
    navigator.clipboard?.writeText(link.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={panelStyle}>
      <div style={headingStyle}>STUDENT'S DOCUMENT LINK</div>
      {error && <div style={{ color: '#f5576c', fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <p style={{ color: '#9aa4c4', fontSize: 12 }}>Loading…</p>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
              background: link.is_valid ? 'rgba(74,222,128,0.15)' : 'rgba(245,87,108,0.15)',
              color: link.is_valid ? '#4ade80' : '#f5576c',
            }}>
              {link.is_valid ? 'Active' : 'Not valid'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input readOnly value={link.link} onFocus={(e) => e.target.select()}
              style={{ flex: 1, minWidth: 220, padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: '#eee', fontSize: 12 }} />
            <button type="button" onClick={handleCopy}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', color: '#333', fontSize: 13, cursor: 'pointer' }}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" disabled={busy} onClick={handleRegenerate}
              style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: '#ddd', fontSize: 12, cursor: busy ? 'wait' : 'pointer' }}>
              Regenerate
            </button>
            {link.is_valid && (
              <button type="button" disabled={busy} onClick={handleRevoke}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #f5576c55', background: 'rgba(245,87,108,0.08)', color: '#f5576c', fontSize: 12, cursor: busy ? 'wait' : 'pointer' }}>
                Revoke
              </button>
            )}
          </div>
          <p style={{ color: '#8a94b8', fontSize: 11, margin: '8px 0 0' }}>
            No login needed — the student opens this to see their document checklist and payment status, and can upload from there too.
            Regenerating kills the old link immediately.
          </p>
        </>
      )}
    </div>
  );
};

export default ChecklistLinkPanel;
